import { safeUpdateDoc, safeDeleteDoc, authedFetch } from '../utils';
import { useState, useRef, useMemo, useEffect } from 'react';
import { Camera, Image as ImageIcon, Music, Plus, Trash2, X, Download, Info, Users, MapPin, CalendarPlus, Search } from 'lucide-react';
import { collection, getDocs, query, where, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { appId } from '../constants';
import CitySearch from './CitySearch';
import { useTags } from '../hooks/useTags';

const getAddedTime = (item) => {
  if (typeof item.createdAt?.toMillis === 'function') return item.createdAt.toMillis();
  if (item.createdAt?.seconds) return item.createdAt.seconds * 1000;
  const parsed = new Date(item.createdAt || 0).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getPhotoTakenTime = (item) => {
  if (!item.exif?.DateTimeOriginal) return getAddedTime(item);
  const parsed = new Date(item.exif.DateTimeOriginal).getTime();
  return Number.isNaN(parsed) ? getAddedTime(item) : parsed;
};

const formatEventDateTime = (event) => {
  if (!event?.startDate) return 'Date unknown';
  const [year, month, day] = event.startDate.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const dateLabel = Number.isNaN(date.getTime())
    ? event.startDate
    : date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

  if (event.isAllDay || !event.startTime) return `${dateLabel} · All day`;

  const [hour, minute] = event.startTime.split(':').map(Number);
  const time = new Date(2000, 0, 1, hour, minute);
  const timeLabel = Number.isNaN(time.getTime())
    ? event.startTime
    : time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${dateLabel} · ${timeLabel}`;
};

const getEventPlace = (event) => {
  if (!event?.placeId) return null;
  return {
    name: event.venueName || event.placeName || event.address || event.placeId,
    address: event.address || null,
    placeId: event.placeId,
    lat: event.addressLat ?? event.lat ?? null,
    lng: event.addressLng ?? event.lng ?? null,
  };
};

const getPhotoPlaceName = (photo) => photo.locationName || photo.locationAddress || photo.locationPlaceId;

export default function MediaView({ photos = [], audioTracks = [], channels = [], allUsers = [], events = [], handleBackgroundUpload, activeUploads = {}, user, b2Token }) {
  const [activeTab, setActiveTab] = useState('global'); // 'global' | 'shared' | 'audio'
  const [sortMethod, setSortMethod] = useState('added'); // 'added' | 'taken'
  const [selectedLibrary, setSelectedLibrary] = useState(null); // channel object
  const [libraryPhotos, setLibraryPhotos] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [eventPickerPhoto, setEventPickerPhoto] = useState(null);
  const [eventSearch, setEventSearch] = useState('');
  const [locationMismatch, setLocationMismatch] = useState(null);
  const [isSavingEventLink, setIsSavingEventLink] = useState(false);
  const { availableTags, getParentTags } = useTags();

  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  // Local state for debounced saves
  const [caption, setCaption] = useState('');
  const [tags, setTags] = useState([]);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  
  // Update local state when selected photo changes
  useEffect(() => {
    if (selectedPhoto) {
      setCaption(selectedPhoto.caption || '');
      setTags(selectedPhoto.tags || []);
    }
  }, [selectedPhoto?.id]);
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const getAuthorizedUrl = (url) => {
    if (!url) return url;
    let fixedUrl = url.replace('f005.backblazeb2.com', 'f000.backblazeb2.com');
    if (fixedUrl.includes('backblazeb2.com') && !b2Token) return null;
    if (!fixedUrl.includes('backblazeb2.com')) return fixedUrl;
    return `${fixedUrl}${fixedUrl.includes('?') ? '&' : '?'}Authorization=${b2Token}`;
  };

  useEffect(() => {
    if (!selectedPhoto) return;
    const currentPhotosList = selectedLibrary ? libraryPhotos : photos;
    if (currentPhotosList.length === 0) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        const idx = currentPhotosList.findIndex(p => p.id === selectedPhoto.id);
        if (idx > 0) setSelectedPhoto(currentPhotosList[idx - 1]);
      } else if (e.key === 'ArrowRight') {
        const idx = currentPhotosList.findIndex(p => p.id === selectedPhoto.id);
        if (idx < currentPhotosList.length - 1) setSelectedPhoto(currentPhotosList[idx + 1]);
      } else if (e.key === 'Escape') {
        setSelectedPhoto(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPhoto, photos, libraryPhotos, selectedLibrary]);

  const sharedLibraries = useMemo(() => {
    if (!channels || !user) return [];
    return channels.filter(c => c.members?.includes(user.uid) || c.participants?.includes(user.uid) || c.id === 'general_chat_global');
  }, [channels, user]);

  const eventsById = useMemo(
    () => new Map(events.map((event) => [event.id, event])),
    [events]
  );

  const matchingEvents = useMemo(() => {
    const queryText = eventSearch.trim().toLowerCase();
    if (!queryText) return [];
    return events
      .filter((event) => (event.title || event.name || '').toLowerCase().includes(queryText))
      .slice(0, 50);
  }, [events, eventSearch]);

  const closeEventPicker = () => {
    if (isSavingEventLink) return;
    setEventPickerPhoto(null);
    setEventSearch('');
    setLocationMismatch(null);
  };

  const saveEventAttachment = async (photo, event, locationUpdates = null) => {
    setIsSavingEventLink(true);
    try {
      const updates = {
        calendarEventId: event.id,
        ...(locationUpdates || {}),
      };
      await safeUpdateDoc(
        doc(db, 'artifacts', appId, 'public', 'data', 'photos', photo.id),
        updates
      );
      setSelectedPhoto((current) => current?.id === photo.id ? { ...current, ...updates } : current);
      setEventPickerPhoto(null);
      setEventSearch('');
      setLocationMismatch(null);
    } catch (error) {
      console.error('Failed to attach calendar event to photo:', error);
      alert('Could not attach that calendar event. Please try again.');
    } finally {
      setIsSavingEventLink(false);
    }
  };

  const handleAttachEvent = (photo, event) => {
    const eventPlace = getEventPlace(event);
    if (!eventPlace) {
      saveEventAttachment(photo, event);
      return;
    }

    const photoHasLocation = Boolean(
      photo.locationPlaceId || photo.locationName || photo.locationAddress
    );
    const locationUpdates = {
      locationName: eventPlace.name,
      locationAddress: eventPlace.address,
      locationPlaceId: eventPlace.placeId,
      locationLat: eventPlace.lat,
      locationLng: eventPlace.lng,
    };

    if (!photoHasLocation) {
      saveEventAttachment(photo, event, locationUpdates);
      return;
    }

    if (photo.locationPlaceId === eventPlace.placeId) {
      saveEventAttachment(photo, event);
      return;
    }

    setLocationMismatch({ photo, event, eventPlace, locationUpdates });
  };

  const handleDetachEvent = async (photo) => {
    setIsSavingEventLink(true);
    try {
      const updates = { calendarEventId: null };
      await safeUpdateDoc(
        doc(db, 'artifacts', appId, 'public', 'data', 'photos', photo.id),
        updates
      );
      setSelectedPhoto((current) => current?.id === photo.id ? { ...current, ...updates } : current);
      setEventPickerPhoto(null);
      setEventSearch('');
    } catch (error) {
      console.error('Failed to detach calendar event from photo:', error);
      alert('Could not detach that calendar event. Please try again.');
    } finally {
      setIsSavingEventLink(false);
    }
  };

  // Fetch photos for selected library
  useEffect(() => {
    if (activeTab !== 'shared' || sharedLibraries.length === 0) {
      if (activeTab !== 'shared') setLibraryPhotos([]);
      return;
    }
    
    let isSubscribed = true;
    const fetchAllSharedPhotos = async () => {
      setLoadingLibrary(true);
      try {
        const extractedPhotos = [];
        
        await Promise.all(sharedLibraries.map(async (channel) => {
          try {
            const isDM = channel.isDM || channel.type === 'dm';
            const friendId = isDM ? (channel.members || channel.participants || []).find(id => id !== user.uid) : null;
            const channelName = isDM ? (allUsers[friendId]?.username || 'DM') : (channel.name || 'Chat');

            const msgsRef = collection(db, 'artifacts', appId, 'public', 'data', 'messages');
            const q = query(msgsRef, where('channelId', '==', channel.id));
            const snap = await getDocs(q);
            
            snap.forEach(doc => {
              const data = doc.data();
              const contentField = data.text !== undefined ? 'text' : (data.content !== undefined ? 'content' : null);
              if (contentField && data[contentField].includes('<img')) {
                const imgRegex = /<img[^>]+src="([^">]+)"/g;
                let match;
                while ((match = imgRegex.exec(data[contentField])) !== null) {
                  extractedPhotos.push({
                    id: `${doc.id}-${match[1].substring(0, 10)}`,
                    url: match[1],
                    name: `From ${channelName}`,
                    createdAt: data.createdAt,
                    senderId: data.userId || data.senderId,
                    isDMPhoto: true
                  });
                }
              }
            });

            // Also check old structure for this channel
            const oldMsgsRef = collection(db, 'channels', channel.id, 'messages');
            const oldQ = query(oldMsgsRef);
            const oldSnap = await getDocs(oldQ).catch(() => ({ docs: [] }));
            
            oldSnap.forEach(doc => {
              const data = doc.data();
              const contentField = data.text !== undefined ? 'text' : (data.content !== undefined ? 'content' : null);
              if (contentField && data[contentField].includes('<img')) {
                const imgRegex = /<img[^>]+src="([^">]+)"/g;
                let match;
                while ((match = imgRegex.exec(data[contentField])) !== null) {
                  extractedPhotos.push({
                    id: `${doc.id}-old-${match[1].substring(0, 10)}`,
                    url: match[1],
                    name: `From ${channelName}`,
                    createdAt: data.createdAt,
                    senderId: data.userId || data.senderId,
                    isDMPhoto: true
                  });
                }
              }
            });
          } catch (e) {
            if (import.meta.env.DEV) console.log(`Could not fetch messages for channel ${channel.id}`, e);
          }
        }));

        if (!isSubscribed) return;

        extractedPhotos.sort((a, b) => {
          const tA = a.createdAt?.seconds || 0;
          const tB = b.createdAt?.seconds || 0;
          return tB - tA;
        });

        setLibraryPhotos(extractedPhotos);
      } catch (err) {
        console.error("Error fetching library photos:", err);
      } finally {
        if (isSubscribed) setLoadingLibrary(false);
      }
    };
    
    fetchAllSharedPhotos();
    return () => { isSubscribed = false; };
  }, [activeTab, sharedLibraries]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    uploadFiles(files);
  };

  const uploadFiles = (files) => {
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        handleBackgroundUpload(file, 'global', 'photo', { uploadId: `photo-${Date.now()}-${file.name}` });
      } else if (file.type.startsWith('audio/')) {
        handleBackgroundUpload(file, 'global', 'audio', { uploadId: `audio-${Date.now()}-${file.name}` });
      } else {
        alert(`${file.name} is not a supported image or audio file.`);
      }
    });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      uploadFiles(Array.from(e.dataTransfer.files));
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const groupedPhotos = useMemo(() => {
    const listToGroup = activeTab === 'shared' ? libraryPhotos : photos;
    
    // Sort array based on sortMethod before grouping
    const sortedList = [...listToGroup].sort((a, b) => {
      if (sortMethod === 'taken') {
        return getPhotoTakenTime(b) - getPhotoTakenTime(a);
      }
      return getAddedTime(b) - getAddedTime(a);
    });

    const groups = {};
    sortedList.forEach(p => {
      const groupTime = sortMethod === 'taken' ? getPhotoTakenTime(p) : getAddedTime(p);
      const dateLabel = groupTime
        ? new Date(groupTime).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })
        : 'Recently Uploaded';

      if (!groups[dateLabel]) groups[dateLabel] = [];
      groups[dateLabel].push(p);
    });
    return groups;
  }, [photos, libraryPhotos, activeTab, sortMethod]);



  return (
    <div 
      className={`flex flex-col h-full bg-white transition-colors duration-200 ${dragActive ? 'bg-blue-50/50' : ''}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <header className="h-14 bg-white border-b border-zinc-200 px-6 flex items-center justify-between shrink-0 z-10">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Camera size={20} className="text-zinc-900" />
            <h1 className="text-sm font-black uppercase tracking-widest text-zinc-900">Media</h1>
          </div>
          
          <div className="h-4 w-px bg-zinc-200 mx-2" />
          
          <div className="flex items-center gap-1 bg-zinc-100 p-1 rounded-xl">
            <button
              onClick={() => { setActiveTab('global'); setSelectedLibrary(null); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${activeTab !== 'audio' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}
              aria-pressed={activeTab !== 'audio'}
            >
              <ImageIcon size={13} strokeWidth={2.5} /> Photos
            </button>
            <button
              onClick={() => { setActiveTab('audio'); setSelectedLibrary(null); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'audio' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}
              aria-pressed={activeTab === 'audio'}
            >
              <Music size={13} strokeWidth={2.5} /> Music
            </button>
          </div>

          {activeTab !== 'audio' && (
            <label className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-zinc-400">
              Photos from
              <select
                value={activeTab}
                onChange={(e) => {
                  setActiveTab(e.target.value);
                  setSelectedLibrary(null);
                }}
                className="bg-zinc-100 border border-zinc-200 outline-none text-[10px] font-bold uppercase tracking-widest text-zinc-600 rounded-lg px-2 py-1.5 cursor-pointer hover:bg-zinc-200 transition-colors"
              >
                <option value="global">Library</option>
                <option value="shared">Shared chats</option>
              </select>
            </label>
          )}
        </div>

        <div className="flex items-center gap-2">
          {(activeTab === 'global' || activeTab === 'shared') && (
            <label className="flex items-center gap-2 mr-4 text-[9px] font-black uppercase tracking-widest text-zinc-400">
              Sort by
              <select
                value={sortMethod}
                onChange={(e) => setSortMethod(e.target.value)}
                className="bg-zinc-100 border border-zinc-200 outline-none text-[10px] font-bold uppercase tracking-widest text-zinc-600 rounded-lg px-2 py-1.5 cursor-pointer hover:bg-zinc-200 transition-colors"
              >
                <option value="added">Added</option>
                <option value="taken">Photo taken</option>
              </select>
            </label>
          )}

          {Object.keys(activeUploads).filter(id => id.startsWith('photo-') || id.startsWith('audio-')).length > 0 && (
             <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-100 rounded-full animate-pulse">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                <span className="text-[8px] font-black uppercase text-blue-600 tracking-widest">Uploading...</span>
             </div>
          )}
          {(activeTab === 'global' || activeTab === 'audio') && (
            <>
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileChange} 
                multiple 
                accept={activeTab === 'audio' ? 'audio/*' : 'image/*'} 
                className="hidden" 
              />
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-1.5 bg-zinc-900 text-white text-[10px] font-black uppercase tracking-widest rounded-lg shadow-lg hover:bg-black transition-all active:scale-95"
              >
                <Plus size={14} /> Upload
              </button>
            </>
          )}
        </div>
      </header>

      <main className="flex-1 overflow-y-auto custom-scrollbar-hidden">
        {activeTab === 'audio' ? (
          <div className="p-6 space-y-4 max-w-3xl mx-auto">
            {audioTracks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 opacity-40">
                <p className="text-xs font-black uppercase tracking-widest text-center">No audio tracks found.</p>
              </div>
            ) : (
              audioTracks.map((track) => (
                <div key={track.id} className="p-4 border border-zinc-200 rounded-xl bg-zinc-50 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0 flex flex-col gap-1">
                    <h3 className="text-sm font-bold text-zinc-900 truncate">{track.title || track.name}</h3>
                    <div className="flex items-center gap-3">
                      <p className="text-[10px] text-zinc-500 uppercase font-black tracking-widest">
                        {track.createdAt?.seconds ? new Date(track.createdAt.seconds * 1000).toLocaleDateString() : ''}
                      </p>
                      <button
                        onClick={async () => {
                          const newState = !track.isSocial;
                          const updateData = { isSocial: newState };
                          if (newState) updateData.socialPostedAt = serverTimestamp();
                          else updateData.socialPostedAt = null;
                          await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'media', track.id), updateData);
                        }}
                        className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded transition-colors ${track.isSocial ? 'bg-blue-100 text-blue-600' : 'bg-zinc-200 text-zinc-500 hover:bg-zinc-300'}`}
                      >
                        {track.isSocial ? 'On Social Feed' : 'Post to Social Feed'}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <audio controls src={track.url} className="h-8" />
                    <button
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to permanently delete this track?')) {
                          await safeDeleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'media', track.id));
                        }
                      }}
                      className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
                      title="Delete Track"
                    >
                      <Trash2 size={16} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : activeTab === 'shared' && libraryPhotos.length === 0 && !loadingLibrary ? (
          <div className="flex flex-col items-center justify-center py-20 opacity-40">
            <Users size={48} strokeWidth={1.5} className="mb-4" />
            <p className="text-xs font-black uppercase tracking-widest text-center">No shared photos found.<br/>Start a chat and send some photos!</p>
          </div>
        ) : (
          <>

            {loadingLibrary ? (
              <div className="flex items-center justify-center py-40">
                <div className="w-8 h-8 border-4 border-zinc-200 border-t-blue-500 rounded-full animate-spin" />
              </div>
            ) : (activeTab === 'global' ? photos : libraryPhotos).length === 0 ? null : (
              <div className="p-6 space-y-10 pb-20">
                {Object.entries(groupedPhotos).map(([date, items]) => (
                  <div key={date} className="space-y-4">
                    <h2 className="text-[10px] font-black uppercase tracking-widest text-zinc-400 px-1">
                      {date}
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-1">
                      {items.map((p) => {
                        const authUrl = getAuthorizedUrl(p.url);
                        const senderName = p.isDMPhoto ? (allUsers[p.senderId]?.username || 'Unknown') : null;
                        const attachedEvent = p.calendarEventId ? eventsById.get(p.calendarEventId) : null;
                        return (
                          <div 
                            key={p.id}
                            onClick={() => setSelectedPhoto(p)}
                            className="aspect-square relative group cursor-pointer overflow-hidden bg-zinc-100"
                          >
                            {authUrl ? (
                              <img 
                                src={authUrl} 
                                alt={p.name}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                loading="lazy"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML += '<div class="absolute inset-0 flex items-center justify-center bg-red-50 text-[8px] text-red-500 font-bold uppercase text-center p-2">Failed to load<br/>' + p.name + '</div>';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full animate-pulse bg-zinc-200" />
                            )}
                            {activeTab === 'global' && !p.isDMPhoto && (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setEventPickerPhoto(p);
                                  setEventSearch('');
                                }}
                                className="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-black/65 text-white hover:bg-black/85 transition-colors"
                                title={p.calendarEventId ? 'Change calendar event' : 'Attach calendar event'}
                                aria-label={p.calendarEventId ? 'Change calendar event' : 'Attach calendar event'}
                              >
                                <CalendarPlus size={13} strokeWidth={2.5} />
                              </button>
                            )}
                            <div className="absolute inset-x-0 bottom-0 min-h-[58%] bg-gradient-to-t from-black/90 via-black/65 to-transparent pointer-events-none grid grid-cols-2 items-end gap-2 p-2 [text-shadow:0_1px_2px_rgb(0_0_0_/_0.9)]">
                               <div className="min-w-0 flex flex-col gap-0.5">
                                 {p.locationName && (
                                   <span className="text-[7px] font-black text-amber-400 uppercase tracking-widest truncate flex items-center gap-1">
                                     <MapPin size={8} className="shrink-0" /> {p.locationName}
                                   </span>
                                 )}
                                 {p.exif?.DateTimeOriginal && (
                                   <span className="text-[7px] font-bold text-zinc-300 uppercase tracking-widest truncate">
                                     {new Date(p.exif.DateTimeOriginal).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                   </span>
                                 )}
                                 <span className="text-[8px] font-bold text-white uppercase tracking-tighter truncate mt-1">
                                   {p.name}
                                 </span>
                                 {senderName && (
                                   <span className="text-[7px] font-bold text-blue-300 uppercase tracking-widest truncate">
                                     From {senderName}
                                   </span>
                                 )}
                               </div>
                               {p.calendarEventId && (
                                 <div className="min-w-0 text-right self-end">
                                   <span className="block text-[8px] font-black text-sky-300 truncate">
                                     {attachedEvent?.title || attachedEvent?.name || 'Event unavailable'}
                                   </span>
                                   {attachedEvent && (
                                     <span className="block text-[7px] font-bold text-zinc-300 truncate mt-0.5">
                                       {formatEventDateTime(attachedEvent)}
                                     </span>
                                   )}
                                 </div>
                               )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {selectedPhoto && (
        <div className="fixed inset-0 z-[9999] bg-black/95 flex flex-col backdrop-blur-sm transition-all duration-300">
          {/* Header */}
          <div className="flex items-center justify-between p-6 shrink-0 bg-gradient-to-b from-black/80 to-transparent">
            <div className="flex flex-col">
              <h2 className="text-xl font-black text-white tracking-tight truncate max-w-xl">
                {selectedPhoto.name}
              </h2>
              <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                {selectedPhoto.createdAt?.seconds 
                  ? new Date(selectedPhoto.createdAt.seconds * 1000).toLocaleString() 
                  : 'Just uploaded'}
              </span>
            </div>
            <button 
              onClick={() => setSelectedPhoto(null)}
              className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors backdrop-blur-md"
            >
              <X size={24} strokeWidth={2.5} />
            </button>
          </div>

          {/* Image Area */}
          <div className="flex-1 overflow-hidden flex items-center justify-center relative px-12">
            {/* Left/Right Nav Arrows */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                const currentPhotosList = selectedLibrary ? libraryPhotos : photos;
                const idx = currentPhotosList.findIndex(p => p.id === selectedPhoto.id);
                if (idx > 0) setSelectedPhoto(currentPhotosList[idx - 1]);
              }}
              className={`absolute left-6 p-4 text-white hover:text-blue-400 transition-colors z-10 ${(selectedLibrary ? libraryPhotos : photos).findIndex(p => p.id === selectedPhoto.id) === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            </button>

            <button 
              onClick={(e) => {
                e.stopPropagation();
                const currentPhotosList = selectedLibrary ? libraryPhotos : photos;
                const idx = currentPhotosList.findIndex(p => p.id === selectedPhoto.id);
                if (idx < currentPhotosList.length - 1) setSelectedPhoto(currentPhotosList[idx + 1]);
              }}
              className={`absolute right-6 p-4 text-white hover:text-blue-400 transition-colors z-10 ${(selectedLibrary ? libraryPhotos : photos).findIndex(p => p.id === selectedPhoto.id) === (selectedLibrary ? libraryPhotos : photos).length - 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </button>

            {getAuthorizedUrl(selectedPhoto.url) ? (
              <img 
                src={getAuthorizedUrl(selectedPhoto.url)} 
                alt={selectedPhoto.name}
                className="max-w-full max-h-full object-contain filter drop-shadow-2xl transition-all duration-300"
              />
            ) : (
              <div className="w-64 h-64 border-4 border-white/10 border-t-blue-500 rounded-full animate-spin" />
            )}
          </div>

          {/* Footer Metadata & Actions */}
          <div className="flex items-center justify-between p-6 shrink-0 bg-gradient-to-t from-black/80 to-transparent">
             <div className="flex items-center gap-8 flex-1">
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-black uppercase text-zinc-500 tracking-widest">Size</span>
                  <span className="text-sm font-bold text-white">
                    {formatSize(selectedPhoto.size)}
                    {selectedPhoto.originalSize && <span className="text-zinc-400 text-xs ml-1">({formatSize(selectedPhoto.originalSize)})</span>}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-black uppercase text-zinc-500 tracking-widest">Format</span>
                  <span className="text-sm font-bold text-white uppercase">{(selectedPhoto.contentType || 'image/jpeg').split('/')[1]}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-black uppercase text-zinc-500 tracking-widest">Taken</span>
                  <span className="text-sm font-bold text-white whitespace-nowrap">
                    {selectedPhoto.exif?.DateTimeOriginal 
                      ? new Date(selectedPhoto.exif.DateTimeOriginal).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) 
                      : 'Unknown'}
                  </span>
                </div>
                <div className="flex flex-col gap-1 w-56 relative z-50">
                  <span className="text-[10px] font-black uppercase text-zinc-500 tracking-widest">Location</span>
                  <CitySearch
                    index={0}
                    minimal={true}
                    city={{
                      name: selectedPhoto.locationName || '',
                      address: selectedPhoto.locationAddress || '',
                      placeId: selectedPhoto.locationPlaceId || null,
                      lat: selectedPhoto.locationLat ?? null,
                      lng: selectedPhoto.locationLng ?? null
                    }}
                    onCityChange={async (idx, place) => {
                      const locationName = place.placeName || place.venueName || place.name || place.address || null;
                      const updates = {
                        locationName,
                        locationAddress: place.address || null,
                        locationPlaceId: place.placeId || null,
                        locationLat: place.lat ?? null,
                        locationLng: place.lng ?? null,
                        ...(place.city ? { locationCity: place.city } : {})
                      };
                      await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'photos', selectedPhoto.id), updates);
                      setSelectedPhoto(prev => ({ ...prev, ...updates }));
                    }}
                    placeholder="Search place..."
                    className="w-full text-xs"
                    types={['geocode']}
                    dropdownUp={true}
                    preferName={true}
                  />
                </div>
                
                {/* Editable Metadata */}
                <div className="flex flex-col gap-2 ml-4 flex-1 max-w-md">
                  <input
                    type="text"
                    value={caption}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCaption(val);
                      if (window.captionTimeout) clearTimeout(window.captionTimeout);
                      window.captionTimeout = setTimeout(async () => {
                        await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'photos', selectedPhoto.id), {
                          caption: val
                        });
                        setSelectedPhoto(prev => ({ ...prev, caption: val }));
                      }, 1000);
                    }}
                    placeholder="Add a caption..."
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-blue-500/50 transition-colors w-full"
                  />
                  <div className="flex flex-wrap gap-2 items-center">
                    {tags.map(tag => (
                      <span key={tag} className="px-2 py-1 bg-white/10 text-white text-[10px] font-black uppercase tracking-widest rounded flex items-center gap-1">
                        {tag}
                        <button 
                          onClick={async () => {
                            const newTags = tags.filter(t => t !== tag);
                            setTags(newTags);
                            await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'photos', selectedPhoto.id), { tags: newTags });
                            setSelectedPhoto(prev => ({ ...prev, tags: newTags }));
                          }}
                          className="hover:text-red-400"
                        ><X size={12} /></button>
                      </span>
                    ))}
                    <div className="relative">
                      <input
                        type="text"
                        value={tagSearchQuery}
                        placeholder="Add Tag +"
                        onFocus={() => setShowTagDropdown(true)}
                        onBlur={() => setTimeout(() => setShowTagDropdown(false), 200)}
                        onChange={(e) => {
                          setTagSearchQuery(e.target.value);
                          setShowTagDropdown(true);
                        }}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter') {
                            const newTag = tagSearchQuery.trim();
                            if (!newTag || tags.includes(newTag)) return;
                            const ancestors = getParentTags(newTag);
                            const newTags = Array.from(new Set([...tags, newTag, ...ancestors]));
                            setTags(newTags);
                            setTagSearchQuery('');
                            setShowTagDropdown(false);
                            await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'photos', selectedPhoto.id), { tags: newTags });
                            setSelectedPhoto(prev => ({ ...prev, tags: newTags }));
                          }
                        }}
                        className="bg-transparent border border-dashed border-white/20 text-white text-[10px] font-black uppercase tracking-widest rounded px-3 py-1.5 outline-none focus:border-white/50 focus:bg-white/10 transition-colors w-32 placeholder:text-zinc-400"
                      />
                      {showTagDropdown && (
                        <div className="absolute bottom-full left-0 mb-1 w-48 max-h-40 overflow-y-auto bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-[100] custom-scrollbar p-1">
                          {availableTags
                            .filter(t => !tags.includes(t) && t.toLowerCase().includes(tagSearchQuery.toLowerCase()))
                            .map(t => (
                              <button
                                key={t}
                                onMouseDown={async (e) => {
                                  e.preventDefault(); // prevent blur
                                  const ancestors = getParentTags(t);
                                  const newTags = Array.from(new Set([...tags, t, ...ancestors]));
                                  setTags(newTags);
                                  setTagSearchQuery('');
                                  setShowTagDropdown(false);
                                  await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'photos', selectedPhoto.id), { tags: newTags });
                                  setSelectedPhoto(prev => ({ ...prev, tags: newTags }));
                                }}
                                className="w-full text-left px-2 py-1.5 text-xs text-zinc-300 hover:bg-white/10 hover:text-white rounded transition-colors"
                              >
                                {t}
                              </button>
                            ))}
                          {availableTags.filter(t => !tags.includes(t) && t.toLowerCase().includes(tagSearchQuery.toLowerCase())).length === 0 && (
                            <div className="px-2 py-1.5 text-xs text-zinc-500 italic">No exact tags found. Press enter to create custom tag.</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
             </div>
             
             <div className="flex items-center gap-4">
               <a 
                 href={getAuthorizedUrl(selectedPhoto.originalUrl || selectedPhoto.url)} 
                 download={selectedPhoto.name}
                 target="_blank"
                 rel="noopener noreferrer"
                 className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white text-xs font-black uppercase tracking-widest rounded-xl backdrop-blur-md transition-all"
               >
                 <Download size={18} strokeWidth={2.5} /> {selectedPhoto.originalUrl ? 'Download High-Res' : 'Download'}
               </a>
               {selectedPhoto.isDMPhoto ? (
                 <div className="flex items-center gap-2 text-[10px] font-black uppercase text-blue-400 tracking-widest px-3 py-2 bg-blue-500/10 rounded-xl backdrop-blur-md">
                   <Info size={14} strokeWidth={3} />
                   Sent by {allUsers[selectedPhoto.senderId]?.username || 'Unknown'}
                 </div>
               ) : (
                 <div className="flex items-center gap-4">
                   <button
                     onClick={async () => {
                       const newState = !selectedPhoto.isSocial;
                       if (newState && !window.confirm('Post this photo to the shared Social Feed?')) return;
                       if (!newState && !window.confirm('Remove this photo from the Social Feed?')) return;
                       
                       setSelectedPhoto(prev => ({ ...prev, isSocial: newState }));
                       
                       const updateData = { isSocial: newState };
                       if (newState) {
                         updateData.socialPostedAt = serverTimestamp();
                       } else {
                         updateData.socialPostedAt = null;
                       }
                       
                       await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'photos', selectedPhoto.id), updateData);
                     }}
                     className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest px-4 py-3 rounded-xl cursor-pointer transition-all backdrop-blur-md border ${
                       selectedPhoto.isSocial 
                         ? 'bg-blue-500/20 text-blue-300 border-blue-500/30 hover:bg-blue-500/30'
                         : 'bg-white/5 text-zinc-300 border-white/5 hover:bg-white/10'
                     }`}
                   >
                     {selectedPhoto.isSocial ? 'Posted to Social' : 'Post to Social Feed'}
                   </button>
                    <button 
                      onClick={async () => {
                        if (window.confirm('Permanently delete this photo?')) {
                           if (selectedPhoto.b2FileId) {
                             const thumbName = selectedPhoto.b2FileName || selectedPhoto.url?.split('/nowyoutreeme/')[1]?.split('?')[0];
                             if (thumbName) {
                               authedFetch('https://us-central1-project-project-30249.cloudfunctions.net/deleteBackblazeFile', {
                                 method: 'POST',
                                 headers: { 'Content-Type': 'application/json' },
                                 body: JSON.stringify({ fileName: thumbName, fileId: selectedPhoto.b2FileId, uid: user.uid })
                               }).catch(err => console.error("Failed to delete thumbnail from B2:", err));
                             }
                           }
                           if (selectedPhoto.originalB2FileId) {
                             const origName = selectedPhoto.originalB2FileName || selectedPhoto.originalUrl?.split('/nowyoutreeme/')[1]?.split('?')[0];
                             if (origName) {
                               authedFetch('https://us-central1-project-project-30249.cloudfunctions.net/deleteBackblazeFile', {
                                 method: 'POST',
                                 headers: { 'Content-Type': 'application/json' },
                                 body: JSON.stringify({ fileName: origName, fileId: selectedPhoto.originalB2FileId, uid: user.uid })
                               }).catch(err => console.error("Failed to delete original from B2:", err));
                             }
                           }
                           await safeDeleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'photos', selectedPhoto.id));
                           setSelectedPhoto(null);
                        }
                      }}
                     className="p-3 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all backdrop-blur-md border border-transparent hover:border-red-500/20"
                   >
                     <Trash2 size={18} strokeWidth={2.5} />
                   </button>
                 </div>
               )}
             </div>
          </div>
        </div>
      )}

      {eventPickerPhoto && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-6">
          <button
            type="button"
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeEventPicker}
            aria-label="Close calendar event search"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="event-picker-title"
            className="relative w-full max-w-xl max-h-[70vh] bg-white border border-zinc-200 rounded-xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
              <div className="min-w-0">
                <h2 id="event-picker-title" className="text-sm font-black text-zinc-900 uppercase tracking-widest">
                  Attach calendar event
                </h2>
                <p className="text-xs text-zinc-500 truncate mt-1">{eventPickerPhoto.name}</p>
              </div>
              <button
                type="button"
                onClick={closeEventPicker}
                className="p-2 text-zinc-400 hover:text-zinc-900 transition-colors"
                aria-label="Close"
              >
                <X size={18} strokeWidth={2.5} />
              </button>
            </div>

            <div className="p-4 border-b border-zinc-200">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="search"
                  value={eventSearch}
                  onChange={(event) => setEventSearch(event.target.value)}
                  placeholder="Search event name"
                  autoFocus
                  className="w-full border border-zinc-300 rounded-lg py-2.5 pl-9 pr-3 text-sm outline-none focus:border-zinc-500"
                />
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-2">
              {!eventSearch.trim() ? (
                <p className="px-3 py-8 text-center text-xs text-zinc-400">Search by event name.</p>
              ) : matchingEvents.length === 0 ? (
                <p className="px-3 py-8 text-center text-xs text-zinc-400">No matching events.</p>
              ) : (
                matchingEvents.map((event) => (
                  <button
                    key={event.id}
                    type="button"
                    disabled={isSavingEventLink}
                    onClick={() => handleAttachEvent(eventPickerPhoto, event)}
                    className="w-full flex items-center justify-between gap-4 rounded-lg px-3 py-3 text-left hover:bg-zinc-100 disabled:opacity-50 transition-colors"
                  >
                    <span className="min-w-0 text-sm font-bold text-zinc-900 truncate">
                      {event.title || event.name || 'Untitled event'}
                    </span>
                    <span className="shrink-0 text-xs font-medium text-zinc-500">
                      {formatEventDateTime(event)}
                    </span>
                  </button>
                ))
              )}
            </div>

            {eventPickerPhoto.calendarEventId && (
              <div className="flex items-center justify-between gap-4 px-5 py-4 border-t border-zinc-200 bg-zinc-50">
                <div className="min-w-0 text-xs text-zinc-500 truncate">
                  Attached: {eventsById.get(eventPickerPhoto.calendarEventId)?.title || eventsById.get(eventPickerPhoto.calendarEventId)?.name || 'Event unavailable'}
                </div>
                <button
                  type="button"
                  disabled={isSavingEventLink}
                  onClick={() => handleDetachEvent(eventPickerPhoto)}
                  className="shrink-0 px-3 py-2 rounded-lg border border-zinc-300 text-[10px] font-black uppercase tracking-widest text-zinc-700 hover:bg-white disabled:opacity-50 transition-colors"
                >
                  Detach
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {locationMismatch && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="location-mismatch-title"
            className="relative w-full max-w-lg bg-white border border-zinc-200 rounded-xl shadow-2xl p-6"
          >
            <h2 id="location-mismatch-title" className="text-sm font-black text-zinc-900 uppercase tracking-widest">
              Different photo location
            </h2>
            <p className="mt-3 text-sm leading-6 text-zinc-600">
              This photo is linked to <strong className="text-zinc-900">{getPhotoPlaceName(locationMismatch.photo)}</strong>. The event is at <strong className="text-zinc-900">{locationMismatch.eventPlace.name}</strong>.
            </p>
            <p className="mt-2 text-xs font-bold text-zinc-500">
              {getPhotoPlaceName(locationMismatch.photo)} → {locationMismatch.eventPlace.name}
            </p>
            <div className="mt-6 grid grid-cols-1 gap-2">
              <button
                type="button"
                disabled={isSavingEventLink}
                onClick={() => saveEventAttachment(
                  locationMismatch.photo,
                  locationMismatch.event,
                  locationMismatch.locationUpdates
                )}
                className="px-4 py-2.5 rounded-lg bg-zinc-900 text-white text-xs font-black uppercase tracking-widest hover:bg-black disabled:opacity-50 transition-colors"
              >
                Change location to {locationMismatch.eventPlace.name}
              </button>
              <button
                type="button"
                disabled={isSavingEventLink}
                onClick={() => saveEventAttachment(locationMismatch.photo, locationMismatch.event)}
                className="px-4 py-2.5 rounded-lg border border-zinc-300 text-zinc-800 text-xs font-black uppercase tracking-widest hover:bg-zinc-50 disabled:opacity-50 transition-colors"
              >
                Keep {getPhotoPlaceName(locationMismatch.photo)} and attach
              </button>
              <button
                type="button"
                disabled={isSavingEventLink}
                onClick={() => setLocationMismatch(null)}
                className="px-4 py-2.5 rounded-lg text-zinc-500 text-xs font-black uppercase tracking-widest hover:bg-zinc-100 disabled:opacity-50 transition-colors"
              >
                Cancel attach
              </button>
            </div>
          </div>
        </div>
      )}

      {dragActive && (
        <div className="absolute inset-0 z-50 pointer-events-none flex items-center justify-center">
           <div className="bg-blue-500/90 backdrop-blur-md text-white p-12 rounded-[40px] shadow-2xl flex flex-col items-center gap-6 scale-110">
              <ImageIcon size={64} strokeWidth={1.5} className="animate-bounce" />
              <span className="text-2xl font-black uppercase tracking-[0.2em]">Drop to Upload</span>
           </div>
        </div>
      )}
    </div>
  );
}
